// Help system registry
//
// This file is the single source of truth for all help article metadata:
// navigation order, groups, titles, and search keywords.
//
// HOW TO ADD A NEW ARTICLE:
//   1. Create the markdown file in src/help/content/ (or content/nodes/)
//   2. Import it in src/help/content/_content.js and add to HELP_CONTENT map
//   3. Add an entry to HELP_ARTICLES below with an id that matches the HELP_CONTENT key
//
// HOW TO ADD A NEW GROUP:
//   Add a new group value string and update getNavGroups() to include it.

import { HELP_CONTENT } from './content/_content'

/** @type {import('./types').HelpArticle[]} */
export const HELP_ARTICLES = [
  // ── Getting Started ────────────────────────────────────────────────────────
  {
    id: 'overview',
    title: 'Overview',
    group: 'getting-started',
    groupLabel: 'Getting Started',
    order: 1,
    icon: 'BookOpen',
    keywords: ['what is', 'workflow', 'intro', 'introduction', 'canvas', 'toolbar', 'log', 'nodes', 'pipeline', 'screen'],
  },
  {
    id: 'quick-start',
    title: 'Quick Start',
    group: 'getting-started',
    groupLabel: 'Getting Started',
    order: 2,
    icon: 'Zap',
    keywords: ['run', 'upload', 'start', 'how to', 'begin', 'first run', 'step by step', 'guide', 'wizard'],
  },

  // ── Using the App ──────────────────────────────────────────────────────────
  {
    id: 'workflow-canvas',
    title: 'Workflow Canvas',
    group: 'using-the-app',
    groupLabel: 'Using the App',
    order: 3,
    icon: 'Layout',
    keywords: ['canvas', 'zoom', 'pan', 'select node', 'minimap', 'status', 'running', 'complete', 'failed', 'layout', 'connections'],
  },
  {
    id: 'toolbar-actions',
    title: 'Toolbar Actions',
    group: 'using-the-app',
    groupLabel: 'Using the App',
    order: 4,
    icon: 'LayoutTemplate',
    keywords: ['run', 'stop', 'pause', 'resume', 'save', 'load', 'export', 'clear', 'toolbar', 'button', 'assistance'],
  },

  // ── Node Guide ─────────────────────────────────────────────────────────────
  {
    id: 'node-guide',
    title: 'Node Guide',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 5,
    icon: 'GitBranch',
    keywords: ['nodes', 'guide', 'pipeline stages', 'steps', 'what each node does'],
  },
  {
    id: 'node-upload-files',
    title: 'Upload Files',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 6,
    icon: 'Upload',
    isNode: true,
    keywords: ['upload', 'zip', 'source', 'cobol files', 'ingest', 'source code', 'copybooks', 'file upload'],
  },
  {
    id: 'node-code-analysis',
    title: 'Code Analysis',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 7,
    icon: 'BarChart3',
    isNode: true,
    keywords: ['analysis', 'parse', 'structure', 'programs', 'copybooks', 'inventory', 'scan', 'read source'],
  },
  {
    id: 'node-code-refactor',
    title: 'Code Refactor',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 8,
    icon: 'Code2',
    isNode: true,
    keywords: ['refactor', 'transform', 'java', 'convert', 'code', 'cobol to java', 'transformation'],
  },
  {
    id: 'node-ai-naming',
    title: 'AI Naming',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 9,
    icon: 'Tag',
    isNode: true,
    keywords: ['naming', 'ai', 'rename', 'semantic', 'methods', 'services', 'intelligent naming', 'names'],
  },
  {
    id: 'node-dependency-mapper',
    title: 'Dependency Mapper',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 10,
    icon: 'GitFork',
    isNode: true,
    keywords: ['dependency', 'map', 'call graph', 'programs', 'relationships', 'dependencies', 'graph'],
  },
  {
    id: 'node-monolith-identifier',
    title: 'Monolith Identifier',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 11,
    icon: 'Scissors',
    isNode: true,
    keywords: ['monolith', 'split', 'boundaries', 'clusters', 'microservices', 'decompose', 'services', 'break up'],
  },
  {
    id: 'node-data-analysis',
    title: 'Data Analysis',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 12,
    icon: 'Database',
    isNode: true,
    keywords: ['data', 'files', 'database', 'vsam', 'db2', 'lineage', 'structures', 'data sources'],
  },
  {
    id: 'node-discovery-roi',
    title: 'Discovery & ROI',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 13,
    icon: 'TrendingUp',
    isNode: true,
    keywords: ['discovery', 'roi', 'estimate', 'effort', 'complexity', 'cost', 'value', 'metrics'],
  },
  {
    id: 'node-architecture',
    title: 'Architecture',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 14,
    icon: 'Cloud',
    isNode: true,
    keywords: ['architecture', 'aws', 'cloud', 'diagram', 'services', 'infrastructure', 'deployment'],
  },
  {
    id: 'node-service-generation',
    title: 'Service Generation',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 15,
    icon: 'Server',
    isNode: true,
    keywords: ['service', 'generate', 'spring boot', 'rest', 'api', 'controllers', 'java services'],
  },
  {
    id: 'node-pre-flight',
    title: 'Pre-Flight Check',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 16,
    icon: 'ShieldCheck',
    isNode: true,
    keywords: ['pre-flight', 'compile', 'check', 'build', 'errors', 'validation', 'compile errors'],
  },
  {
    id: 'node-java-packages',
    title: 'Java Packages',
    group: 'node-guide',
    groupLabel: 'Node Guide',
    order: 17,
    icon: 'Archive',
    isNode: true,
    keywords: ['java', 'package', 'spring boot', 'artifact', 'download', 'zip', 'deliverable', 'final output'],
  },

  // ── Results & Support ──────────────────────────────────────────────────────
  {
    id: 'results-deliverables',
    title: 'Results & Deliverables',
    group: 'results-support',
    groupLabel: 'Results & Support',
    order: 18,
    icon: 'Download',
    keywords: ['results', 'download', 'deliverables', 'artifacts', 'reports', 'output', 'get files'],
  },
  {
    id: 'errors-troubleshooting',
    title: 'Errors & Troubleshooting',
    group: 'results-support',
    groupLabel: 'Results & Support',
    order: 19,
    icon: 'AlertCircle',
    keywords: ['error', 'failed', 'blocked', 'troubleshoot', 'fix', 'retry', 'decision', 'execution failed'],
  },
  {
    id: 'advanced-mode',
    title: 'Advanced Mode',
    group: 'results-support',
    groupLabel: 'Results & Support',
    order: 20,
    icon: 'Wrench',
    keywords: ['advanced', 'mode', 'compile report', 'technical', 'details', 'pre-flight details'],
  },
  {
    id: 'restrictions',
    title: 'Restrictions & Guardrails',
    group: 'results-support',
    groupLabel: 'Results & Support',
    order: 21,
    icon: 'Lock',
    keywords: ['locked', 'disabled', 'enabled', 'language', 'delete', 'guardrails', 'pilot', 'cannot change'],
  },
  {
    id: 'faq',
    title: 'FAQ',
    group: 'results-support',
    groupLabel: 'Results & Support',
    order: 22,
    icon: 'HelpCircle',
    keywords: ['faq', 'questions', 'why', 'how', 'cannot', 'common', 'frequently asked'],
  },
  {
    id: 'support',
    title: 'Contact & Support',
    group: 'results-support',
    groupLabel: 'Results & Support',
    order: 23,
    icon: 'MessageSquare',
    keywords: ['contact', 'support', 'report issue', 'help', 'email', 'collect', 'bug', 'problem'],
  },
]

// ── Navigation groups (ordered) ────────────────────────────────────────────

const NAV_GROUP_ORDER = ['getting-started', 'using-the-app', 'node-guide', 'results-support']

/**
 * Returns articles grouped for navigation.
 * @returns {{ id: string, label: string, articles: import('./types').HelpArticle[] }[]}
 */
export function getNavGroups() {
  const map = {}
  for (const article of HELP_ARTICLES) {
    if (!map[article.group]) {
      map[article.group] = { id: article.group, label: article.groupLabel, articles: [] }
    }
    map[article.group].articles.push(article)
  }
  // Sort each group's articles by order
  for (const g of Object.values(map)) {
    g.articles.sort((a, b) => a.order - b.order)
  }
  return NAV_GROUP_ORDER.map(id => map[id]).filter(Boolean)
}

/**
 * Returns a single article by ID.
 * @param {string} id
 * @returns {import('./types').HelpArticle | undefined}
 */
export function getArticleById(id) {
  return HELP_ARTICLES.find(a => a.id === id)
}

/**
 * Returns the raw markdown content for an article ID.
 * @param {string} id
 * @returns {string}
 */
export function getArticleContent(id) {
  return HELP_CONTENT[id] || ''
}

/**
 * Searches articles by title, keywords, and markdown body content.
 * Returns matched articles sorted by relevance (title match first).
 * @param {string} query
 * @returns {import('./types').HelpArticle[]}
 */
export function searchArticles(query) {
  if (!query || !query.trim()) return []
  const q = query.trim().toLowerCase()

  const scored = HELP_ARTICLES.map(article => {
    const titleMatch = article.title.toLowerCase().includes(q)
    const keywordMatch = article.keywords.some(k => k.toLowerCase().includes(q))
    const contentMatch = (HELP_CONTENT[article.id] || '').toLowerCase().includes(q)

    if (!titleMatch && !keywordMatch && !contentMatch) return null

    // Score: title=3, keyword=2, content=1
    const score = (titleMatch ? 3 : 0) + (keywordMatch ? 2 : 0) + (contentMatch ? 1 : 0)
    return { article, score }
  }).filter(Boolean)

  scored.sort((a, b) => b.score - a.score)
  return scored.map(s => s.article)
}
